import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StageBadge } from "@/components/Badges";
import { fmtDateTime } from "@/lib/date";
import {
  Activity as ActivityIcon, UserPlus, Ticket, BookOpen, ArrowRight, CalendarClock,
  Calendar, MapPin, CheckCircle2, Clock3, Info,
} from "lucide-react";
import ScopePicker, { useScopeOptions, MonthPicker, monthKey } from "@/components/ScopePicker";
import TeamReportCard from "@/components/team/TeamReportCard";
import StpCard from "@/components/stp/StpCard";
import CountryPrompt from "@/components/CountryPrompt";
import { REPORT_ROLES } from "@/components/team/shared";
import BootcampHubCard from "@/components/bootcamp/BootcampHubCard";

const COUNTRY_LABEL = { AU: "Australia", NZ: "New Zealand", AU_NZ: "Australia + New Zealand" };

function fmtConfRange(startIso, endIso) {
  if (!startIso && !endIso) return "Dates TBC";
  try {
    const s = startIso ? new Date(startIso) : null;
    const e = endIso ? new Date(endIso) : null;
    if (s && e) {
      if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
        return `${s.getDate()}–${e.getDate()} ${s.toLocaleDateString("en-AU", { month: "short", year: "numeric" })}`;
      }
      const o = { day: "numeric", month: "short", year: "numeric" };
      return `${s.toLocaleDateString("en-AU", o)} – ${e.toLocaleDateString("en-AU", o)}`;
    }
    return (s || e).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "Dates TBC"; }
}

const METRIC_LABELS = {
  conversations: "Conversations",
  pointed_to_solution: "Pointed to Solution",
  contact_exchanges: "Contact Exchanges",
  mg0s: "MG0s",
  dtms: "DTMs",
  mg1s: "MG1s",
  launches: "Launches",
};

const _loadStr = (k, fallback) => {
  try { return localStorage.getItem(k) || fallback; } catch { return fallback; }
};

export default function Home() {
  const { user, isReadOnly, isViewAs, scopes } = useAuth();
  const [funnel, setFunnel] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [current, setCurrent] = useState({ au: null, nz: null });

  const { options: scopeOpts } = useScopeOptions();
  const defaultMonth = monthKey();
  const [activityMonth, setActivityMonth] = useState(() => _loadStr("home_activity_month", defaultMonth));
  const [pboMonth, setPboMonth] = useState(() => _loadStr("home_pbo_month", defaultMonth));
  const [activityScope, setActivityScope] = useState(() => _loadStr("home_activity_scope", "personal"));
  const [pboScope, setPboScope] = useState(() => _loadStr("home_pbo_scope", "personal"));

  // Clamp stored scope to what's currently available.
  useEffect(() => {
    if (!scopeOpts.length) return;
    const valid = new Set(scopeOpts.map((o) => o.value));
    if (!valid.has(activityScope)) setActivityScope("personal");
    if (!valid.has(pboScope)) setPboScope("personal");
  }, [scopeOpts, activityScope, pboScope]);
  useEffect(() => { try { localStorage.setItem("home_activity_month", activityMonth); } catch {} }, [activityMonth]);
  useEffect(() => { try { localStorage.setItem("home_pbo_month", pboMonth); } catch {} }, [pboMonth]);
  useEffect(() => { try { localStorage.setItem("home_activity_scope", activityScope); } catch {} }, [activityScope]);
  useEffect(() => { try { localStorage.setItem("home_pbo_scope", pboScope); } catch {} }, [pboScope]);

  // Activity fetch — depends ONLY on activityMonth + activityScope.
  useEffect(() => {
    api.get("/activity/report", { params: { scope: activityScope, period: "month", month: activityMonth } })
      .then((r) => setFunnel(r.data.funnel))
      .catch(() => setFunnel(null));
  }, [activityScope, activityMonth]);

  // Dashboard (PBO snapshot) fetch — depends ONLY on pboMonth + pboScope.
  useEffect(() => {
    api.get("/dashboard", { params: { scope: pboScope, month: pboMonth } })
      .then((r) => setDashboard(r.data))
      .catch(() => setDashboard(null));
  }, [pboScope, pboMonth]);

  // Conferences — never affected by month/scope.
  const loadCurrent = () => api.get("/conferences/current").then((r) => setCurrent(r.data || { au: null, nz: null })).catch(() => {});
  useEffect(() => { loadCurrent(); }, []);

  const displayName = (user?.name || "").trim() || "there";
  const upcoming = (dashboard?.upcoming_meetings || [])[0];
  const isOversight = user?.role === "oversight";
  const canLogActivity = !isReadOnly && !isOversight;
  const canAddPbo = !isReadOnly && !isOversight;

  return (
    <div className="space-y-6" data-testid="home-page">
      {/* Welcome hero — premium navy with gold accents */}
      <section
        data-testid="home-welcome"
        className="relative overflow-hidden rounded-2xl bg-[#0A1F44] text-white shadow-sm ring-1 ring-[#C9A961]/30"
      >
        <div className="absolute -right-24 -bottom-24 w-72 h-72 rounded-full border border-[#C9A961]/15" aria-hidden />
        <div className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full border border-[#C9A961]/10" aria-hidden />
        <div className="relative p-5 sm:p-7">
          <div className="flex items-start gap-4">
            <img
              src="/logo.png"
              alt="Team Smith Hub"
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl shadow-md ring-1 ring-[#C9A961]/40 shrink-0"
            />
            <div className="min-w-0">
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">
                Welcome to Team Smith Hub
              </div>
              <h1 className="font-serif text-2xl sm:text-3xl leading-tight mt-1 break-words [overflow-wrap:anywhere]" data-testid="home-greeting">
                Hi {displayName} — your central place to build, track and grow.
              </h1>
            </div>
          </div>
          <p className="text-white/75 text-sm sm:text-[15px] leading-relaxed mt-4 max-w-2xl">
            Track your conversations and activity, manage your PBOs, stay on top of upcoming conferences,
            and access the training and resources you need to build and grow your business.
          </p>
          <div className="mt-4 flex items-center gap-2 sm:gap-3 text-[10px] sm:text-[11px] uppercase tracking-[0.26em] text-[#C9A961]">
            <span>Connect</span>
            <span className="text-[#C9A961]/40">·</span>
            <span>Build</span>
            <span className="text-[#C9A961]/40">·</span>
            <span>Grow</span>
            <span className="text-[#C9A961]/40">·</span>
            <span>Lead</span>
          </div>
        </div>
      </section>

      {/* This month snapshot */}
      <Card className="p-5" data-testid="home-this-month">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="font-serif text-lg text-[#0A1F44]">Activity</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <ScopePicker value={activityScope} onChange={setActivityScope} options={scopeOpts} testid="home-activity-scope" />
            <MonthPicker value={activityMonth} onChange={setActivityMonth} testid="home-activity-month" />
            <Link to="/activity" className="text-xs text-slate-500 hover:text-[#0A1F44] flex items-center gap-1">
              View activity <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(METRIC_LABELS).map(([k, label]) => (
            <div key={k} className="rounded-lg border border-slate-200 p-3 bg-white" data-testid={`home-metric-${k}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#C9A961] font-semibold">{label}</div>
              <div className="font-serif text-2xl text-[#0A1F44] mt-1 tabular-nums">{funnel?.[k] ?? "—"}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="home-quick-actions">
        {canLogActivity && <QA to="/activity" icon={ActivityIcon} label="Log activity" testid="qa-activity" />}
        {canAddPbo && <QA to="/pbos/new" icon={UserPlus} label="Add PBO" testid="qa-add-pbo" />}
        <QA to="/conferences" icon={Ticket} label="Conference tickets" testid="qa-conferences" />
        <QA to="/hub" icon={BookOpen} label="Resources" testid="qa-hub" />
      </div>

      {isViewAs && <BootcampHubCard />}
      <CountryPrompt onSaved={loadCurrent} />
      <StpCard />
      {REPORT_ROLES.includes(user?.role) && <TeamReportCard />}

      {/* PBO snapshot */}
      {dashboard && (
        <Card className="p-5" data-testid="home-pbo-snapshot">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="font-serif text-lg text-[#0A1F44]">My PBOs</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <ScopePicker value={pboScope} onChange={setPboScope} options={scopeOpts} testid="home-pbo-scope" />
              <MonthPicker value={pboMonth} onChange={setPboMonth} testid="home-pbo-month" />
              <Link to="/pbos" className="text-xs text-slate-500 hover:text-[#0A1F44] flex items-center gap-1">
                View PBOs <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Mini label="Active in process" value={dashboard.counts?.active} testid="pbo-active" />
            <Mini label="MG1s / new PBOs" value={dashboard.counts?.new_month} testid="pbo-new-month" />
            <Mini label="Launches this month" value={dashboard.counts?.launched_month} testid="pbo-launched" />
            <Mini label="Dropped this month" value={dashboard.counts?.dropped_month} testid="pbo-dropped" />
          </div>
          {dashboard.data_quality?.missing_mg1_timestamp > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900" data-testid="pbo-data-quality">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{dashboard.data_quality.note}</span>
            </div>
          )}
          {upcoming && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <CalendarClock className="w-4 h-4 text-[#C9A961] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Next appointment</div>
                  <div className="text-sm text-slate-900 truncate">
                    <Link to={`/pbos/${upcoming.pbo_id}`} className="hover:text-[#0A1F44]">{upcoming.pbo_name}</Link>
                    <span className="text-slate-400 mx-1">·</span>
                    <StageBadge stage={upcoming.next_meeting_stage || upcoming.current_stage} />
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(upcoming.next_meeting_at)}</div>
            </div>
          )}
        </Card>
      )}

      {/* Conference snapshot — next active conference per country the user can see */}
      {(current.au || current.nz) && (
        <Card className="p-5" data-testid="home-conf-snapshot">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg text-[#0A1F44]">Next conference</h2>
            <Link to="/conferences" className="text-xs text-slate-500 hover:text-[#0A1F44] flex items-center gap-1">
              Open <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(() => {
              const seen = new Set();
              const items = [];
              const push = (cc, conf) => {
                if (!conf || seen.has(conf.id)) return;
                seen.add(conf.id);
                const badge = conf.country === "AU_NZ" ? "AU_NZ" : cc.toUpperCase();
                items.push({ cc: badge, conf });
              };
              (current.order || ["AU", "NZ"]).forEach((cc) => push(cc, current[cc.toLowerCase()]));
              return items.map(({ cc, conf }) => {
              const purchased = conf.my_status?.ticket_status === "purchased";
              return (
                <Link
                  key={conf.id}
                  to="/conferences"
                  className="rounded-lg border border-slate-200 p-3 hover:border-[#C9A961] transition-colors"
                  data-testid={`home-conf-${cc}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961] font-semibold">
                    {COUNTRY_LABEL[cc]}
                  </div>
                  <div className="text-sm font-medium text-slate-900 mt-0.5">{conf.name}</div>
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-600">
                    <Calendar className="w-3 h-3 text-[#C9A961]" />
                    <span>{fmtConfRange(conf.starts_on, conf.ends_on)}</span>
                  </div>
                  {conf.city && (
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600">
                      <MapPin className="w-3 h-3 text-[#C9A961]" />
                      <span>{conf.city}</span>
                    </div>
                  )}
                  <div className="mt-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${
                      purchased ? "bg-[#C9A961]/15 text-[#6B5A1D]" : "bg-slate-100 text-slate-600"
                    }`}>
                      {purchased ? <CheckCircle2 className="w-3 h-3" /> : <Clock3 className="w-3 h-3" />}
                      {purchased ? "Ticket purchased" : "Ticket: not yet"}
                    </span>
                  </div>
                </Link>
              );
              });
            })()}
          </div>
        </Card>
      )}
    </div>
  );
}

function QA({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid}>
      <Card className="p-4 h-full hover:border-[#C9A961] transition-colors">
        <div className="flex flex-col items-start gap-2">
          <div className="w-10 h-10 rounded-lg bg-[#0A1F44] text-[#C9A961] flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <div className="text-sm font-medium text-slate-900">{label}</div>
        </div>
      </Card>
    </Link>
  );
}

function Mini({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#C9A961] font-semibold">{label}</div>
      <div className="font-serif text-2xl text-[#0A1F44] mt-1 tabular-nums">{value ?? "—"}</div>
    </div>
  );
}
